import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  getRolePositions, assignRolePosition, removeRolePosition, updateRolePositionScope,
} from '../../../services/roleService';
import { positionAsync } from '../../positions/config';
import DataTable from '../../../components/ui/DataTable';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import {
  buildDataScopeFields, toDataScopePayload, dataScopeInitialValues, formatDataScope, DATA_SCOPE_TYPES,
} from '../dataScopeSchema';

// Role bağlı pozisyonlar. Bir pozisyona rol bağlandığında o pozisyonu AÇIK atamayla
// işgal eden herkes rolü taşır; kişi değiştiğinde rolün yeniden tanımlanması gerekmez.
//
// Yazma yolu TEK: rol üzerinden. (Pozisyon tarafında detay sayfası yok.)
export default function RolePositionsTab({
  roleId, canWrite, canEditScopes = true, isEnterpriseAdminRole = false, scopeLockNotice,
}) {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(true);
  // AKM POC §9.3 — ekleme ve kapsam düzenleme çekmeceleri (tablo dışında ekleme satırı YOK).
  const [addOpen, setAddOpen] = useState(false);
  const [scopeRow, setScopeRow] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState({ isOpen: false, row: null });
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setFetching(true);
    try {
      const res = await getRolePositions(roleId);
      const r = res.data?.data;
      setRows(Array.isArray(r) ? r : []);
    } finally {
      setFetching(false);
    }
  }, [roleId]);

  useEffect(() => { load(); }, [load]);

  // false dönerse çekmece açık kalır (hata interceptor'da gösterilir).
  const handleAssign = useCallback(async (formData) => {
    if (!formData?.positionId) return false;
    const scope = isEnterpriseAdminRole ? { dataScopeType: DATA_SCOPE_TYPES.DEFAULT } : toDataScopePayload(formData);
    const res = await assignRolePosition(roleId, { positionId: formData.positionId, ...scope });
    if (res?.data?.status === false) return false;
    await load();
    return true;
  }, [roleId, load, isEnterpriseAdminRole]);

  const handleSaveScope = useCallback(async (formData) => {
    if (!scopeRow?.id) return false;
    const res = await updateRolePositionScope(roleId, scopeRow.id, toDataScopePayload(formData));
    if (res?.data?.status === false) return false;
    await load();
    return true;
  }, [roleId, scopeRow, load]);

  const addSchema = useMemo(() => [
    { name: 'positionId', label: t('role.addPosition'), type: 'asyncSelect', grid: 12, required: true, ...positionAsync },
    ...(isEnterpriseAdminRole
      ? [{ name: 'eaNotice', type: 'notice', content: scopeLockNotice || t('dataScope.enterpriseAdminAlwaysCompany') }]
      : buildDataScopeFields(t)),
  ], [t, isEnterpriseAdminRole, scopeLockNotice]);

  const scopeSchema = useMemo(() => buildDataScopeFields(t, scopeRow?.dataScopeTargets || []), [t, scopeRow]);
  const scopeInitialValues = useMemo(() => (scopeRow ? dataScopeInitialValues(scopeRow) : undefined), [scopeRow]);

  const handleRemoveClick = useCallback((row) => {
    setRemoveConfirm({ isOpen: true, row });
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setRemoving(true);
    try {
      await removeRolePosition(roleId, removeConfirm.row.id);
      await load();
    } catch { /* interceptor */ }
    setRemoveConfirm({ isOpen: false, row: null });
    setRemoving(false);
  }, [roleId, removeConfirm.row, load]);

  const columns = useMemo(() => {
    const base = [
      { name: 'id', label: 'ID', options: { display: 'excluded', filter: false, sort: false } },
      {
        name: 'positionLabel',
        label: t('role.position'),
        options: { filter: false, sort: false },
      },
      {
        name: 'occupantCount',
        label: t('role.occupantCount'),
        options: { filter: false, sort: false },
      },
      {
        name: 'dataScope',
        label: t('dataScope.title'),
        options: {
          filter: false, sort: false,
          customBodyRender: (_, tableMeta) => {
            const row = rows[tableMeta.rowIndex];
            if (!row) return '';
            return isEnterpriseAdminRole ? t('dataScope.types.company') : formatDataScope(t, row);
          },
        },
      },
    ];

    if (!canWrite) return base;

    return [
      ...base,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = rows[tableMeta.rowIndex];
            const actions = isEnterpriseAdminRole || !canEditScopes ? [] : [
              { type: 'edit', label: t('dataScope.editScope'), onClick: (r) => setScopeRow(r) },
            ];
            actions.push({ type: 'delete', label: t('role.removePosition'), onClick: handleRemoveClick });
            return <OptionsMenu actions={actions} row={row} />;
          },
        },
      },
    ];
  }, [t, canWrite, canEditScopes, rows, handleRemoveClick, isEnterpriseAdminRole]);

  return (
    <Container>
      <Hint>{t('role.positionsHint')}</Hint>

      <GenericFormDrawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={t('role.addPosition')}
        schema={addSchema}
        onSave={handleAssign}
      />

      <GenericFormDrawer
        open={!!scopeRow}
        onClose={() => setScopeRow(null)}
        title={t('dataScope.editScope')}
        schema={scopeSchema}
        initialValues={scopeInitialValues}
        onSave={handleSaveScope}
      />

      <ConfirmDialog
        isOpen={removeConfirm.isOpen}
        title={t('role.removePosition')}
        message={t('role.removePositionMessage', {
          name: removeConfirm.row?.positionLabel || '',
          count: removeConfirm.row?.occupantCount || 0,
        })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setRemoveConfirm({ isOpen: false, row: null })}
        loading={removing}
      />

      <DataTable
        title={t('role.positions')}
        fetching={fetching}
        columns={columns}
        data={rows}
        totalRowCount={rows.length}
        pageIndex={0}
        pageSize={rows.length || 10}
        serverSide={false}
        canCreateNewRecord={!!canWrite}
        newButtonLabel={t('role.addPosition')}
        onNewButtonClick={() => setAddOpen(true)}
      />
    </Container>
  );
}

// Tek kaydırma: yüksekliği doldurur, kaydırma yalnız DataTable satırlarında.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const Hint = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
  margin-bottom: 12px;
`;

